import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

export interface MultiSelectOption<T extends string> {
  value: T;
  label: string;
}

interface MultiSelectDropdownProps<T extends string> {
  label: string;
  options: ReadonlyArray<MultiSelectOption<T>>;
  selected: T[];
  onChange: (next: T[]) => void;
}

/**
 * Compact multi-select filter (VIS-201): a button showing the count, opening a
 * checkbox popover. Closes on outside click. Kept intentionally small — the
 * directory needs several of these and none warrant a heavyweight combobox dep.
 */
export function MultiSelectDropdown<T extends string>({
  label,
  options,
  selected,
  onChange,
}: MultiSelectDropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {return;}
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {setOpen(false);}
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const toggle = (value: T) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm',
          'border-secondary-300 bg-white dark:border-secondary-600 dark:bg-secondary-800 dark:text-white',
          selected.length > 0 && 'border-primary-400 text-primary-700 dark:text-primary-300'
        )}
      >
        <span className="truncate">
          {label}
          {selected.length > 0 && ` (${selected.length})`}
        </span>
        <ChevronDownIcon className="h-4 w-4 flex-shrink-0 text-secondary-400" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute z-20 mt-1 max-h-64 w-56 overflow-auto rounded-lg border border-secondary-200 bg-white p-1 shadow-lg dark:border-secondary-700 dark:bg-secondary-800"
        >
          {options.map((opt) => (
            <label
              key={opt.value}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-secondary-50 dark:text-secondary-200 dark:hover:bg-secondary-700"
            >
              <input
                type="checkbox"
                className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                checked={selected.includes(opt.value)}
                onChange={() => toggle(opt.value)}
              />
              <span className="truncate">{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default MultiSelectDropdown;
